import React, { useMemo } from 'react';
import { MESSAGE_TIP_STATUS } from '../../../../../shared/constants/message';
import { formatAmountWithTwoDecimalPlaces } from '../../../../../shared/utils/function';

/**
 * Builds the chat header subtitle (subscriber total spend, recent spend). Priority is shown in MessageHeader.
 * @param {Object} params
 * @param {boolean} params.loadingRoomInfo
 * @param {boolean} params.hasPriority
 * @param {boolean} params.isCreator
 * @param {{ total?: number, recentSpend?: number, isFirstMessageTip?: boolean, isCreatorReplyToTip?: boolean, firstTipState?: number }|undefined} params.destUserInfo
 * @returns {React.ReactNode} Subtitle content or null
 */
export function useChatHeaderSubtitle({
  loadingRoomInfo,
  hasPriority,
  isCreator,
  destUserInfo,
}) {
  const subscriberTotalSpend = useMemo(() => {
    const raw = destUserInfo?.total;
    return typeof raw === 'number' && !Number.isNaN(raw) ? raw : null;
  }, [destUserInfo?.total]);

  const recentSpend = useMemo(() => {
    const raw = destUserInfo?.recentSpend;
    return typeof raw === 'number' && !Number.isNaN(raw) ? raw : null;
  }, [destUserInfo?.recentSpend]);

  // First-message tips are pending until the creator replies; don't treat them as spent yet.
  const isFirstTipAwaitingReply =
    destUserInfo?.firstTipState === MESSAGE_TIP_STATUS.PENDING_CREATOR_REPLY ||
    (Boolean(destUserInfo?.isFirstMessageTip) &&
      !Boolean(destUserInfo?.isCreatorReplyToTip) &&
      !hasPriority);

  const headerSubtitle = useMemo(() => {
    if (loadingRoomInfo) return null;

    const parts = [];

    if (
      isCreator &&
      !isFirstTipAwaitingReply &&
      subscriberTotalSpend != null &&
      subscriberTotalSpend > 0
    ) {
      const formattedTotal = subscriberTotalSpend.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      const spendClassName = hasPriority
        ? 'subscriber_total_spend_priority'
        : 'subscriber_total_spend';

      parts.push(
        <span key="total-spend" className={spendClassName}>
          {formattedTotal}
          {' '}
          spent
        </span>
      );

      if (recentSpend != null && recentSpend > 0) {
        parts.push(
          <span key="recently-spent" className="recently_spent_text">
            Recently spent $
            {formatAmountWithTwoDecimalPlaces(recentSpend)}
          </span>
        );
      }
    }

    if (!parts.length) return null;

    return parts.map((part, index) => (
      <React.Fragment key={index}>
        {index > 0 && (
          <span className="subtitle_bullet" aria-hidden="true">
            &bull;
          </span>
        )}
        {part}
      </React.Fragment>
    ));
  }, [loadingRoomInfo, hasPriority, isCreator, isFirstTipAwaitingReply, subscriberTotalSpend, recentSpend]);

  return headerSubtitle;
}
